import React, { useState } from "react";
import ParcelIcon from "../../../helper/svg/parcel.svg";
import ResrvationIcon from "../../../helper/svg/reservationicon.svg";
import Incomeicon from "../../../helper/svg/incomeicon.svg";
import { LuUsersRound } from "react-icons/lu";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Pagination from "../../../components/common/Pagination";
import { getAllOrders } from "../../../api/owner/restaurants/reservations";
import moment from "moment";
import { getAllNotifications } from "../../../api/common/notifications";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  getDashboardInsights,
  getorderReports,
} from "../../../api/owner/dashboard";
import { PieChart, Pie, Cell } from "recharts";
import { useTranslation } from "../../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[978px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const limit = 10;
  const [selectedOrder, setSelectedOrder] = useState({});

  const {
    data: ordersData,
    isLoading: loading,
    isError: orderError,
    error: orderErrorDetails,
  } = useQuery({
    queryKey: ["restaurantOrders", page],
    queryFn: () => getAllOrders({ page, limit }),
    keepPreviousData: true,
  });

  const { data: ordersReportData } = useQuery({
    queryKey: ["orders-report"],
    queryFn: getorderReports,
    keepPreviousData: true,
  });

  const {
    data: notificationsData,
    isLoading: loadingNotifications,
    isError: errorNotifications,
  } = useQuery({
    queryKey: ["notifications"],
    queryFn: getAllNotifications,
    keepPreviousData: true,
  });

  const {
    data: InsightsData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["dashboardInsights"],
    queryFn: getDashboardInsights,
    keepPreviousData: true,
  });

  const insights = InsightsData?.data;
  const orders = ordersData?.data?.orders;
  const pagination = ordersData?.data?.pagination;
  const notifications = notificationsData?.data?.notifications || [];
  const chartData = ordersReportData?.data?.report || [];

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const chartDataModified = (chartData) => {
    return chartData.map((entry) => ({
      name: months[entry.month - 1],
      thisYear: entry.currentYear,
      lastYear: entry.previousYear,
    }));
  };

  const transformedData = chartDataModified(chartData);

  const openModel = (order) => {
    setSelectedOrder(order);
    setIsPopupOpen(true);
  };

  const data = [
    { name: "Active Users", value: 400 },
    { name: "Inactive Users", value: 300 },
    { name: "Pending Users", value: 200 },
  ];

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28"];

  return (
    <>
      {/* =================Card Section================ */}
      <div className="grid xl:grid-cols-4 sm:grid-cols-2 grid-col-1 sm:gap-[16px] gap-[10px] ">
        {/* 1st card */}
        <div className="flex justify-between 2xl:p-[25px] lg:p-[20px] p-[15px] border border-[#E4E4E4] rounded-[10px] bg-[#fff] items-center">
          <div className="flex 2xl:gap-[18px] gap-[10px] items-center">
            <img
              src={ParcelIcon}
              alt="logo"
              className="2xl:w-[37px] w-[20px]"
            />
            <p className="2xl:text-xl text-base font-semibold lg:leading-[30px] leading-[25px]">
              {t("userdashboard").cards.ordersToday}
            </p>
          </div>
          <div>
            <p className="2xl:text-xl text-base font-semibold text-[#4CAF50]">
              {insights?.ordersTodayCount}
            </p>
          </div>
        </div>
        {/* 2nd card */}
        <div className="flex justify-between 2xl:p-[25px] lg:p-[20px] p-[15px] border border-[#E4E4E4] rounded-[10px] bg-[#fff] items-center">
          <div className="flex 2xl:gap-[18px] gap-[10px] items-center">
            <img
              src={ResrvationIcon}
              alt="logo"
              className="2xl:w-[37px] w-[20px]"
            />
            <p className="2xl:text-xl text-base font-semibold lg:leading-[30px] leading-[25px]">
              {t("userdashboard").cards.bookingsToday}
            </p>
          </div>
          <div>
            <p className="2xl:text-xl text-base font-semibold text-[#4CAF50]">
              {insights?.bookingsTodayCount}
            </p>
          </div>
        </div>
        {/* 3rd card */}
        <div className="flex justify-between 2xl:p-[25px] lg:p-[20px] p-[15px] border border-[#E4E4E4] rounded-[10px] bg-[#fff] items-center">
          <div className="flex 2xl:gap-[18px] gap-[10px] items-center">
            <img
              src={Incomeicon}
              alt="logo"
              className="2xl:w-[37px] w-[20px]"
            />
            <p className="2xl:text-xl text-base font-semibold lg:leading-[30px] leading-[25px]">
              {t("userdashboard").cards.revenueToday}
            </p>
          </div>
          <div>
            <p className="2xl:text-xl text-base font-semibold text-[#4CAF50]">
              {insights?.totalRevenue
                ? ` ₾${Number(insights?.totalRevenue || 0).toFixed(2)}`
                : 0}
            </p>
          </div>
        </div>
        {/* 4th card */}
        <div className="flex justify-between 2xl:p-[25px] lg:p-[20px] p-[15px] border border-[#E4E4E4] rounded-[10px] bg-[#fff] items-center">
          <div className="flex 2xl:gap-[18px] gap-[10px] items-center">
            <LuUsersRound className="text-[#FFA808] 2xl:text-[30px] text-[20px]" />
            {/* <img
              src={ResrvationIcon}
              alt="logo"
              className="2xl:w-[37px] w-[20px]"
            /> */}
            <p className="2xl:text-xl text-base font-semibold lg:leading-[30px] leading-[25px]">
              {t("userdashboard").cards.activeStaff}
            </p>
          </div>
          <div>
            <p className="2xl:text-xl text-base font-semibold text-[#4CAF50]">
              {insights?.activeStaffCount}
            </p>
          </div>
        </div>
      </div>

      {/* =================Graph Section================ */}
      <div className="grid 2xl:grid-cols-[434px_1fr_485px] lg:grid-cols-3 md:grid-cols-2 grid-col-1 sm:gap-[21px] gap-[10px] mt-[24px]">
        {/* 1st graph */}
        <div className="p-[24px] border-[#E0E0E0] rounded-[16px] bg-[#fff] min-h-[318px] border w-full">
          <h2 className="text-lg font-medium text-black">
            {t("userdashboard").charts.totalOrders}
          </h2>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart
              data={transformedData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(value) => `${value}`} />
              <Tooltip formatter={(value) => `${value}`} />
              <Legend />
              <Line
                type="monotone"
                dataKey="thisYear"
                stroke="#FFA500"
                strokeWidth={1.5}
                name="This Year"
              />
              <Line
                type="monotone"
                dataKey="lastYear"
                stroke="#28a745"
                strokeDasharray="5 5"
                strokeWidth={1.5}
                name="Last Year"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="p-[24px] border-[#E0E0E0] rounded-[16px] bg-[#fff] min-h-[318px] border w-full ">
          <h2 className="text-lg font-medium text-black">
            {t("userdashboard").charts.engagementStats}
          </h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="50%"
                outerRadius="70%"
                fill="#8884d8"
                label
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="2xl:p-[24px] p-[15px] border-[#E0E0E0] rounded-[16px] bg-[#fff] min-h-[318px] border w-full">
          <h2 className="2xl:text-lg text-base font-medium text-black mb-[16px]">
            {t("userdashboard").notifications.title}
          </h2>

          <ul className="flex flex-col gap-[3px] max-h-[250px] overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.map((item, index) => (
                <li
                  key={item._id || index}
                  className="flex flex-col text-base text-[#18181B] py-[8px]"
                >
                  {item.message}
                  <span className="text-xs opacity-[40%]">
                    {new Date(item.createdAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </li>
              ))
            ) : (
              <li className="text-[16px] text-[#999] py-[8px] text-center">
                {t("header").noNotifications}
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* =================Recent order Section================ */}
      <div className="mt-[31px]">
        <h2 className="text-[22px] font-medium">
          {t("userdashboard").orders.title}
        </h2>
        <div className="rounded-[10px] border border-[#D9D9D9] overflow-hidden mt-[28px]">
          <div className="overflow-x-auto ">
            <table className="min-w-full bg-white ">
              <thead className="bg-[#FFFAF2] ">
                <tr className="">
                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("userdashboard").orders.orderId}
                  </th>
                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("userdashboard").orders.customerName}
                  </th>
                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("userdashboard").orders.status}
                  </th>
                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("userdashboard").orders.totalAmount}
                  </th>

                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("userdashboard").orders.action}
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders && orders.length > 0 ? (
                  orders.map((order, index) => (
                    <tr
                      key={order._id || index}
                      className="border-t border-[#E4E4E4]"
                    >
                      <td className="py-[16px] px-[32px] text-[18px] font-normal text-black">
                        {order.orderId}
                      </td>
                      <td className="py-[16px] px-[32px] text-[18px] font-normal text-black whitespace-nowrap">
                        {order.userId?.name || "N/A"}
                      </td>
                      <td className="py-[16px] px-[32px] text-[18px] font-normal text-black whitespace-nowrap">
                        {order.paymentStatus || "Pending"}
                      </td>
                      <td className="py-[16px] px-[32px] text-[18px] font-normal whitespace-nowrap">
                        ₾{Number(order.totalAmount || 0).toFixed(2)}
                      </td>

                      <td className="py-[27px] px-[32px]">
                        <button
                          onClick={() => openModel(order)}
                          className="text-[#4CAF50] underline font-normal"
                        >
                          {t("userdashboard").orders.view}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-gray-500">
                      {t("userdashboard").orders.noOrders}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {orders?.length > 0 && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              setPage={setPage}
            />
          )}
        </div>
      </div>

      <PopupModal isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <div className="w-full rounded-[20px] md:p-[30px] py-[20px] px-[15px]">
          <div className="grid grid-col-1 sm:grid-cols-3 justify-between text-left pb-[21px] border-b border-[#EEEEEE] mb-[18px]">
            <h2 className="text-[22px] font-medium col-span-2">
              {t("userdashboard").modal.orderDetail}
            </h2>
          </div>
          <div className="h-[350px] overflow-y-auto">
            <p className="text-[17px] font-medium mb-[15px]">
              {t("userdashboard").modal.forOrderId} {selectedOrder?.orderId} -{" "}
              {selectedOrder?.userId?.name}'s Order
            </p>
            <div className="border border-[#DDDDDD] rounded-[5px] xl:gap-[47px] gap-[20px] p-[20px] pb-[36px] bg-white grid lg:grid-cols-[auto_auto_1fr] md:grid-cols-2 grid-cols-1">
              <div>
                <h2 className="text-[17px] font-medium text-black">
                  {t("userdashboard").modal.orderInformation}
                </h2>
                <ul className="mt-5">
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("userdashboard").modal.orderId}:
                    </span>
                    {selectedOrder?.orderId}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("userdashboard").modal.name}
                    </span>
                    {selectedOrder?.userId?.name}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("userdashboard").modal.totalGuests}:
                    </span>
                    {selectedOrder?.reservationId?.totalGuest}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("userdashboard").modal.foodType}:
                    </span>{" "}
                    {selectedOrder?.reservationId?.foodType}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("userdashboard").modal.mealType}
                    </span>{" "}
                    {selectedOrder?.reservationId?.mealType}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("userdashboard").modal.slotTime}
                    </span>{" "}
                    01:00 PM
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("userdashboard").modal.orderDate}
                    </span>{" "}
                    {moment(selectedOrder?.reservationId?.bookingDate).format(
                      "MM/DD/YYYY"
                    )}
                  </li>
                </ul>
              </div>
              <div>
                <h2 className="text-[17px] font-medium text-black">
                  {t("userdashboard").modal.paymentInformation}
                </h2>
                <ul className="mt-5">
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("userdashboard").modal.paymentMethod}
                    </span>
                    {selectedOrder?.paymentMethod}
                  </li>
                  <li className="text-[17px]  mb-[10px] text-[#FF9D00]">
                    <span className="font-medium text-[#757474]">
                      {t("userdashboard").modal.paymentStatus}:
                    </span>{" "}
                    {selectedOrder?.paymentStatus}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("userdashboard").modal.subTotal}:
                    </span>{" "}
                    ₾{selectedOrder?.subtotal}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium text-orange-500">
                      {t("userdashboard").modal.discount}:{" "}
                    </span>{" "}
                    ₾{selectedOrder?.discountAmount}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium text-orange-500">
                      {t("userdashboard").modal.tokenCharges}:{" "}
                    </span>{" "}
                    ₾{selectedOrder?.tokenCharges}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("userdashboard").modal.total}:
                    </span>{" "}
                    ₾{selectedOrder?.totalAmount}
                  </li>
                </ul>
              </div>
              <div>
                <h2 className="text-[17px] font-medium text-black">
                  {t("userdashboard").modal.orderStatus}
                </h2>
                <ul className="mt-5">
                  <li className="text-[17px]  mb-[10px] text-[#FF9D00]">
                    <span className="font-medium text-[#757474]">
                      {t("userdashboard").modal.currentStatus}:
                    </span>{" "}
                    {selectedOrder?.orderStatus}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default Dashboard;
